<template>
    <!-- ДЕЛОВАЯ ИСТОРИЯ ВЕЩИ: приняли, выдали, вернули, увезли, списали. Не путать
         с технической историей рядом — та отвечает на вопрос «кто какое поле поменял»
         и нужна поддержке, а не кладовщику. -->
    <div class="inventory-lifecycle">
        <div class="inventory-lifecycle__loader" v-if="isLoading">
            <VSpinner></VSpinner>
        </div>

        <template v-else>
            <p class="inventory-lifecycle__error" v-if="error">{{ error }}</p>

            <!-- ПУСТАЯ ЛЕНТА — ШТАТНОЕ СОСТОЯНИЕ, а не сбой: у оборудования, заведённого
                 до появления жизненного цикла, событий нет и не будет. Восстанавливать
                 их по техническому журналу значило бы выдать догадку о дате приёмки
                 и прежних держателях за факт. -->
            <p class="inventory-lifecycle__empty" v-else-if="!events.length">
                История жизненного цикла пока отсутствует
            </p>

            <ul class="inventory-lifecycle__list" v-else>
                <li
                    class="inventory-lifecycle__point inventory-lifecycle-point"
                    :key="event.id"
                    v-for="event in events"
                >
                    <!-- Маркер, а не иконка на каждый тип: восемь разных значков в ленте
                         из трёх строк читаются хуже, чем восемь оттенков одной формы —
                         глаз ловит цвет раньше, чем рисунок. -->
                    <span
                        class="inventory-lifecycle-point__marker"
                        :class="`inventory-lifecycle-point__marker_${marker(event)}`"
                        aria-hidden="true"
                    ></span>

                    <div class="inventory-lifecycle-point__body">
                        <!-- Дата со временем, а не одна дата: события одного дня иначе
                             не различить, а порядок в ленте становится необъяснимым. -->
                        <div class="inventory-lifecycle-point__header">
                            <h4 class="inventory-lifecycle-point__title">{{ title(event) }}</h4>

                            <span class="inventory-lifecycle-point__occurred-at" :title="event.occurred_at">
                                {{ $_toLocalDateShort(event.occurred_at) }}
                            </span>
                        </div>

                        <!-- Что именно изменилось: «Иванов Иван → Петров Пётр».
                             Подпись у каждого перехода своя — у события может измениться
                             и держатель, и город, и статус сразу, и три безымянные
                             строки «А → Б» подряд не читаются вовсе. -->
                        <ul class="inventory-lifecycle-point__transitions" v-if="transitions(event).length">
                            <li
                                class="inventory-lifecycle-point__transition"
                                :key="transition.key"
                                v-for="transition in transitions(event)"
                            >
                                <span class="inventory-lifecycle-point__transition-label">
                                    {{ transition.label }}
                                </span>

                                <span class="inventory-lifecycle-point__transition-value">
                                    {{ transition.from }}
                                    <span class="inventory-lifecycle-point__arrow" aria-hidden="true">→</span>
                                    <span class="inventory-lifecycle-point__transition-to">{{ transition.to }}</span>
                                </span>
                            </li>
                        </ul>

                        <p class="inventory-lifecycle-point__comment" v-if="event.comment">
                            Комментарий: {{ event.comment }}
                        </p>

                        <p class="inventory-lifecycle-point__actor">{{ actor(event) }}</p>
                    </div>
                </li>
            </ul>
        </template>
    </div>
</template>
<script>
// Components
import VSpinner from '../../../components/VProgress/VSpinner.vue';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { getInventoryItemLifecycle } from '../../../../js/inventory-api';
import { errorMessage } from '../../../../js/error';
import { actorName, eventTitle, markerModifier, transitionsOf } from '../../../../js/inventoryLifecycle';

/**
 * Лента жизненного цикла оборудования (7-F1).
 *
 * ТОЛЬКО ЧТЕНИЕ. Кнопки переходов — «Выдать», «Вернуть», «Переместить», «В ремонт»,
 * «Списать» — появятся в 7-F2 и лягут одной транзакцией вместе с изменением самого
 * предмета. Здесь заложено основание: сущность, маршрут и лента.
 *
 * СТРАНИЦ НЕТ. Событий жизненного цикла у предмета единицы за всю его жизнь — меньше,
 * чем правок его карточки за один день. Кнопка «Показать ещё» здесь означала бы
 * механизм ради случая, которого не бывает.
 *
 * Решения о содержании строки — какие переходы показывать, как назвать пустую сторону,
 * каким маркером метить — живут в js/inventoryLifecycle.js: там они и проверяются
 * тестами, потому что компонент в них не монтируется.
 */
@Component({
    components: {
        VSpinner,
    },
})
class VInventoryItemLifecycle extends Vue {
    @Prop({ type: Number, required: true })
    itemId;

    events = [];
    isLoading = false;
    error = '';

    title(event) {
        return eventTitle(event);
    }

    marker(event) {
        return markerModifier(event);
    }

    transitions(event) {
        return transitionsOf(event);
    }

    actor(event) {
        return actorName(event);
    }

    async fetch() {
        this.isLoading = true;
        this.error = '';

        try {
            const response = await getInventoryItemLifecycle(this.itemId);

            this.events = response.data.data ?? [];
        } catch (requestError) {
            // Ошибку показываем внутри вкладки, а не модалкой поверх карточки.
            this.error = errorMessage(requestError);
            this.events = [];
        } finally {
            this.isLoading = false;
        }
    }

    mounted() {
        this.fetch();
    }
}

export default VInventoryItemLifecycle;
</script>
<style lang="scss">
.inventory-lifecycle {
    // Цвета маркеров: событие узнаётся по оттенку раньше, чем прочитано название.
    // Те же значения, что у статусов оборудования и счётчиков списка, — чтобы «в ремонте»
    // в ленте и «в ремонте» в таблице были одного цвета.
    --lifecycle-received: #2e7d32;
    --lifecycle-issued: #1a73e8;
    --lifecycle-returned: #00897b;
    --lifecycle-holder: #5f6368;
    --lifecycle-moved: #7b48c8;
    --lifecycle-repair: #ef6c00;
    --lifecycle-writeoff: #d93025;
    --lifecycle-unknown: #9aa0a6;

    &__loader {
        display: flex;
        justify-content: center;
        padding: 24px 0;
    }

    &__error {
        margin: 0 0 12px;
        color: #d93025;
        font-size: 0.875rem;
    }

    // Честное пустое состояние, а не спрятанный блок: «истории нет» — это сведения,
    // а исчезнувший раздел выглядит как поломка.
    &__empty {
        margin: 0;
        padding: 20px 0;
        color: #9aa0a6;
        font-size: 0.875rem;
    }

    &__list {
        margin: 0;
        padding: 0;
        list-style: none;
    }
}

.inventory-lifecycle-point {
    display: flex;
    gap: 12px;
    padding: 12px 0;

    & + & {
        border-top: 1px solid #eef1f4;
    }

    // Маркер выровнен по первой строке названия, а не по центру блока: у события
    // с тремя переходами центр оказывается на уровне города, и лента «плывёт».
    &__marker {
        flex: 0 0 auto;
        width: 10px;
        height: 10px;
        margin-top: 5px;
        border-radius: 50%;
        background: var(--lifecycle-unknown);
    }

    &__marker_received {
        background: var(--lifecycle-received);
    }

    &__marker_issued {
        background: var(--lifecycle-issued);
    }

    &__marker_returned {
        background: var(--lifecycle-returned);
    }

    &__marker_holder_changed {
        background: var(--lifecycle-holder);
    }

    &__marker_moved {
        background: var(--lifecycle-moved);
    }

    // Неисправность — из той же семьи, что и ремонт: вещь не в строю. Снятие отметки —
    // возвращение в строй, тем же цветом, что и возврат на склад.
    &__marker_marked_broken,
    &__marker_repair_sent,
    &__marker_repair_returned {
        background: var(--lifecycle-repair);
    }

    &__marker_fault_cleared {
        background: var(--lifecycle-returned);
    }

    &__marker_written_off {
        background: var(--lifecycle-writeoff);
    }

    &__body {
        flex: 1 1 auto;
        min-width: 0;
    }

    // Название и дата в одной строке, пока есть ширина; на узкой панели дата уходит
    // под название сама — карточка живёт в панели 28…50% области, и медиазапросами
    // её ширина не описывается.
    &__header {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 4px 12px;
    }

    &__title {
        margin: 0;
        color: #202124;
        font-size: 0.9375rem;
        font-weight: 600;
        line-height: 1.3;
    }

    &__occurred-at {
        color: #9aa0a6;
        font-size: 0.75rem;
        white-space: nowrap;
    }

    &__transitions {
        margin: 6px 0 0;
        padding: 0;
        list-style: none;
    }

    &__transition {
        display: flex;
        flex-wrap: wrap;
        gap: 2px 8px;
        padding: 2px 0;
        font-size: 0.8125rem;
        line-height: 1.35;
    }

    &__transition-label {
        flex: 0 0 auto;
        color: #9aa0a6;
    }

    // min-width: 0 и перенос по словам: имена держателей бывают длиннее панели,
    // и без этого строка растягивает карточку и включает горизонтальную прокрутку.
    &__transition-value {
        min-width: 0;
        color: #5f6368;
        overflow-wrap: anywhere;
    }

    &__arrow {
        margin: 0 2px;
        color: #c4c7ca;
    }

    // Новое состояние — тёмным: именно оно отвечает на вопрос «где вещь сейчас».
    &__transition-to {
        color: #202124;
        font-weight: 500;
    }

    &__comment {
        margin: 6px 0 0;
        color: #5f6368;
        font-size: 0.8125rem;
        line-height: 1.35;
        overflow-wrap: anywhere;
    }

    &__actor {
        margin: 6px 0 0;
        color: #9aa0a6;
        font-size: 0.75rem;
    }
}
</style>
